// https://stackoverflow.com/a/175787
// isNaN ist faster than regex https://www.measurethat.net/Benchmarks/Show/5502/0/isnumber-regex-vs-isnan
export const isNumeric = str => {
	if (typeof str != 'string') return false; // we only process strings!
	return (
		!isNaN(str) && !isNaN(parseFloat(str)) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
	); // ...and ensure strings of whitespace fail
};

export function getPriceInterval(type, count = 1) {
	switch (type) {
		case 'day':
			return 'Täglich';
		case 'week':
			return 'Wöchentlich';
		case 'month':
			if (count === 3) return 'Alle 3 Monate';
			else if (count === 6) return 'Alle 6 Monate';
			return 'Monatlich';
		case 'year':
			return 'Jährlich';
	}
}

export function getPaymentStatus(type) {
	switch (type) {
		case 'active':
			return 'Zahlung erfolgt';
		case 'unpaid':
			return 'Zahlung ausstehend';
		case 'past_due':
			return 'Überfällig';
		case 'pending':
			return 'Rechnungsstellung noch nicht erfolgt';
		case 'canceled':
			return 'Abonnement gekündigt'; // Subscription canceled
		default:
			return 'Unbekannter Status';
	}
}

export function getBrokerCaseStatus(type) {
	switch (type) {
		case 'open':
			return 'Offen';
		case 'closed':
			return 'Geschlossen';
		case 'new':
			return 'Neu';
		case 'processing':
			return 'In Bearbeitung';
		case 'succeed':
			return 'Erledigt';
		case 'failed':
			return 'Abgelehnt';
		default:
			'In Bearbeitung';
	}
}

export function getSubscriptionStatus(subscription) {
	if (!subscription) {
		return 'Keine aktive Abonnements'; // No active subscriptions
	}

	if (subscription.status === 'canceled') {
		return 'Abonnement gekündigt'; // Subscription canceled
	}

	const currentDate = new Date();
	const currentPeriodStart = new Date(subscription.current_period_start); // Assumes date in ISO format
	const currentPeriodEnd = new Date(subscription.current_period_end); // Assumes date in ISO format

	if (currentDate < currentPeriodStart) {
		return 'Abonnement noch nicht gestartet'; // Subscription not started yet
	}

	if (currentDate > currentPeriodEnd) {
		return 'Abonnement abgelaufen'; // Subscription expired
	}

	const latestInvoice = subscription.latest_invoice;

	if (latestInvoice) {
		switch (latestInvoice.status) {
			case 'paid':
				if (latestInvoice.amount_due === latestInvoice.amount_paid) {
					return 'Zahlung erfolgt'; // Payment successful
				} else {
					return 'Teilzahlung erfolgt'; // Partial payment made
				}
			case 'void':
				return 'Zahlung wurde storniert';
			case 'open':
			case 'unpaid':
				return 'Zahlung ausstehend'; // Payment pending
			case 'past_due':
				return 'Überfällig'; // Payment overdue
			case 'pending':
				return 'Rechnungsstellung noch nicht erfolgt'; // Invoice not yet issued
			default:
				return 'Unbekannter Status'; // Unknown status
		}
	} else {
		return 'Rechnung wird ertellt..';
	}
}

export function getAccountSettingsNotificationName(notificationName) {
	switch (notificationName) {
		case 'damage_case_created':
			return this.$t('damage_case_stored');
			break;
		case 'created_repair_extension':
			return this.$t('applied_repair_extension');
			break;
		case 'assigned_damage_assessor_to_repair_shop':
			return this.$t('changed_assigned_assessors');
			break;
		case 'assigned_lawyer_office_to_repair_shop':
			return this.$t('changed_assigned_lawyers');
			break;
		case 'mandate_ended_early':
			return this.$t('mandate_ended_early');
			break;
		case 'new_customer_chat_message':
			return this.$t('new_chat_messages');
			break;
		case 'document_signed_by_customer':
			return this.$t('customer_signed_documents');
			break;
		case 'distribute_damage_case_to_assessors':
			return this.$t('admin_distributed_new_damage_case');
			break;
		case 'final_calculation_stored':
			return this.$t('uploaded_final_calculation');
			break;
		case 'pre_calculation_stored':
			return this.$t('uploaded_pre_calculation');
			break;
		case 'expertise_stored':
			return this.$t('uploaded_expertise');
			break;
		case 'damage_case_payment_receipt_saved':
			return this.$t('new_payment_receipt_added_by_assessor');
			break;
		case 'document_stored':
			return this.$t('uploaded_new_document');
			break;
		case 'rental_car_classification_stored':
			return this.$t('new_rental_car_classified');
			break;
		case 'updated_repair_extension_status':
			return this.$t('status_change_repair_extension');
			break;
	}
}

export function parsePaymentDeadlineData(paymentDays) {
	switch (paymentDays) {
		case '14_days':
			return 14;
		case '30_days':
			return 30;
		case '60_days':
			return 60;
		case '90_days':
			return 90;
	}
}

export function isDunningDocument(documentType) {
	switch (documentType) {
		case 'payment_reminder_customer_without_lawyer':
		case 'payment_reminder_customer_with_lawyer':
		case 'second_payment_reminder_customer_without_lawyer':
		case 'second_payment_reminder_customer_with_lawyer':
		case 'last_dunning_notice':
		case 'first_dunning_notice':
			return true;
		default:
			return false;
	}
}

export function getL2AdminID() {
	return 5;
}

/**
 * Format a Date to YYYY-MM-DD string in local timezone
 * Use this when sending date-only fields to the API
 * @param {Date} date
 * @returns {string} "2025-01-12"
 */
export function toLocalDateString(date) {
	if (!date || !(date instanceof Date)) return null;

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

/**
 * Format a Date to YYYY-MM-DD HH:mm:ss string in local timezone
 * Use this when sending datetime fields to the API
 * @param {Date} date
 * @returns {string} "2025-01-12 14:30:00"
 */
export function toLocalDateTimeString(date) {
	if (!date || !(date instanceof Date)) return null;

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');

	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function fromUnixTimestamp(timestamp) {
	return new Date(timestamp * 1000);
}
